import _ from 'lodash';
import { action, computed, observable, makeObservable } from 'mobx';
import {DomainObject} from 'shared/store';
import {t} from 'shared/core';
import TimelineEntry from './TimelineEntry';
import {dateFormatter, fullName} from 'shared/tools';
import {FunnelStage} from './FunnelStage';
import AttachedDocument from 'shared/stores/documents/AttachedDocument';
import Answer from './Answer';
import Position from './Position';
import CandidateTag from './CandidateTag';
import {Offer} from '../offers';
import {User} from '../users';
import AiScreenerCandidateResult from 'main/stores/recruiting/AiScreenerCandidateResult';

class Candidate extends DomainObject {
  id;
  createdAt;
  firstName;
  lastName;
  email;
  phoneNumber;
  additionalInformation;
  resume;
  currentStage;
  disqualified;
  linkedinUrl;
  timelineEntries = [];
  avatarInitials;
  avatarColour;
  position;
  answers = [];
  origin;
  recaptchaResponse;
  sendDisqualifiedEmail;
  offer;
  canSendOffer;
  canEditOffer;
  score;
  referredByUser;
  candidateTags = [];

  _relations = {
    currentStage: FunnelStage,
    resume: AttachedDocument,
    answers: [Answer],
    position: Position,
    offer: Offer,
    referredByUser: User,
    candidateTags: [CandidateTag],
    aiScreenerResult: AiScreenerCandidateResult
  };

  constructor(other) {
    super();

    makeObservable(this, {
      id: observable,
      createdAt: observable,
      firstName: observable,
      lastName: observable,
      email: observable,
      phoneNumber: observable,
      additionalInformation: observable,
      resume: observable,
      currentStage: observable,
      disqualified: observable,
      linkedinUrl: observable,
      timelineEntries: observable,
      avatarInitials: observable,
      avatarColour: observable,
      position: observable,
      answers: observable,
      origin: observable,
      recaptchaResponse: observable,
      sendDisqualifiedEmail: observable,
      offer: observable,
      canSendOffer: observable,
      canEditOffer: observable,
      score: observable,
      referredByUser: observable,
      candidateTags: observable,
      hasResume: computed,
      clearResume: action,
      updateResume: action,
      date: computed,
      urlSlug: computed,
      name: computed,
      sortedAnswers: computed,
      simplifiedOrigin: computed,
      originView: computed,
      mostRecentRating: computed
    });

    this.merge(other);
  }

  get hasResume() {
    return _.has(this.resume, 'fileName');
  }

  clearResume() {
    this.resume = null;
  }

  updateResume(file) {
    if (file) {
      this.merge({resume: file});
    } else {
      this.clearResume();
    }
  }

  _afterMerge() {
    if (this.timelineEntries) {
      this.timelineEntries = this.timelineEntries.map(entry => TimelineEntry.fromJS(entry));
    }
  }

  get date() {
    return dateFormatter(this.createdAt);
  }

  get urlSlug() {
    return _.kebabCase(this.name);
  }

  get name() {
    return fullName(this);
  }

  get sortedAnswers() {
    return _.orderBy(this.answers, 'question.order');
  }

  get simplifiedOrigin() {
    if (this.origin === 'sourced') return this.origin;

    return 'applied';
  }

  get originView() {
    if (_.includes(['sourced', 'applied', 'linkedin'], this.origin)) {
      return t(`recruiting.candidate.origins.${this.origin}`);
    } else {
      return _.capitalize(this.origin);
    }
  }

  get mostRecentRating() {
    return _.chain(this.timelineEntries)
      .filter((entry) => entry.isRating)
      .orderBy('createdAt', 'desc')
      .head()
      .value();
  }
}

export default Candidate;
